"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, TrendingUp, Wallet, Home, Receipt } from "lucide-react";

import { Slider } from "@/components/ui/slider";
import { buttonClasses } from "@/components/ui/button";
import { trackCalculatorUsed } from "@/lib/analytics/events";
import {
  calculateAffordability,
  describePropertyTier,
} from "@/lib/calculator/affordability";
import { formatUsd, formatUsdCompact } from "@/lib/format";

const SAVINGS_DEFAULT = 15_000;
const SAVINGS_MIN = 5_000;
const SAVINGS_MAX = 75_000;
const SAVINGS_STEP = 1_000;

const MONTHLY_DEFAULT = 1_000;
const MONTHLY_MIN = 300;
const MONTHLY_MAX = 3_000;
const MONTHLY_STEP = 50;

/**
 * Interactive affordability calculator. Two sliders, computed result card,
 * shareable. Fires `calculator_used` PostHog event after the user has
 * settled on their inputs (debounced) so we collect distribution data
 * without spamming on every drag.
 */
export function AffordabilityCalculator() {
  const [savings, setSavings] = useState(SAVINGS_DEFAULT);
  const [monthly, setMonthly] = useState(MONTHLY_DEFAULT);

  const result = useMemo(
    () => calculateAffordability({ savingsUsd: savings, monthlyUsd: monthly }),
    [savings, monthly]
  );

  const tier = useMemo(
    () => describePropertyTier(result.maxPriceUsd),
    [result.maxPriceUsd]
  );

  // Debounced analytics: only fire once the user has stopped sliding for 2s.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      trackCalculatorUsed({
        savings,
        monthly,
        resulting_price: result.maxPriceUsd,
        binding_constraint: result.bindingConstraint,
      });
    }, 2000);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [savings, monthly, result.maxPriceUsd, result.bindingConstraint]);

  return (
    <div className="space-y-10">
      {/* Inputs */}
      <div className="space-y-8 rounded-3xl bg-white p-7 shadow-sm ring-1 ring-ink-200 sm:p-9">
        <SliderField
          id="savings"
          label="¿Cuánto tienes ahorrado?"
          value={savings}
          min={SAVINGS_MIN}
          max={SAVINGS_MAX}
          step={SAVINGS_STEP}
          onChange={setSavings}
          formatValue={formatUsd}
          hint="Lo que tienes hoy disponible para el inicial"
        />
        <SliderField
          id="monthly"
          label="¿Cuánto puedes pagar al mes?"
          value={monthly}
          min={MONTHLY_MIN}
          max={MONTHLY_MAX}
          step={MONTHLY_STEP}
          onChange={setMonthly}
          formatValue={formatUsd}
          hint="Aporte cómodo durante 24 meses de construcción"
        />
      </div>

      {/* Result */}
      <div className="rounded-3xl bg-ink p-8 text-cream-50 shadow-lg sm:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">
          Tu rango de inversión
        </p>
        <h2 className="mt-4 text-display-sm font-extrabold text-cream-50">
          Hasta {formatUsd(result.maxPriceUsd)}
        </h2>
        {result.belowMarketEntry ? (
          <p className="mt-4 max-w-xl text-base leading-relaxed text-cream-100/85">
            Estás cerca del piso del mercado pre-construcción en RD. Con un
            poco más de ahorro o capacidad mensual, abres muchas más opciones.
            Aún así, podemos mostrarte alternativas en zonas emergentes.
          </p>
        ) : (
          <p className="mt-4 max-w-xl text-base leading-relaxed text-cream-100/85">
            Esto es lo que puedes comprar en pre-construcción CONFOTUR.{" "}
            <strong className="text-cream-50">
              Ejemplo típico: {tier.type} en {tier.zone}.
            </strong>
          </p>
        )}

        {/* Breakdown */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <BreakdownStat
            icon={<Wallet className="h-5 w-5" aria-hidden />}
            label="Inicial al firmar"
            value={formatUsd(result.downPaymentUsd)}
            sub="20% del precio"
          />
          <BreakdownStat
            icon={<Receipt className="h-5 w-5" aria-hidden />}
            label="Mensual durante 24m"
            value={formatUsd(result.monthlyDuringConstructionUsd)}
            sub="60% del precio"
          />
          <BreakdownStat
            icon={<Home className="h-5 w-5" aria-hidden />}
            label="Saldo a la entrega"
            value={formatUsd(result.balloonAtDeliveryUsd)}
            sub="20%, refinanciable"
          />
          <BreakdownStat
            icon={<TrendingUp className="h-5 w-5" aria-hidden />}
            label="Renta estimada"
            value={`${formatUsd(result.estimatedMonthlyRentUsd)}/mes`}
            sub="cap rate ~7.5%"
          />
        </div>

        {/* Long-term wins */}
        <div className="mt-10 grid gap-6 border-t border-cream-50/15 pt-8 sm:grid-cols-2">
          <Stat
            label="Ahorro CONFOTUR (15 años)"
            value={formatUsdCompact(result.confoturSavings15yUsd)}
            note="0% impuesto a la propiedad por 15 años"
          />
          <Stat
            label="Valor proyectado en 5 años"
            value={formatUsdCompact(result.projectedValue5yUsd)}
            note="al 7% de plusvalía anual"
          />
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col items-start gap-4 rounded-2xl bg-coral/10 p-6 ring-1 ring-coral/30 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-md">
            <p className="font-semibold text-cream-50">
              Quiero ver propiedades reales en este rango.
            </p>
            <p className="mt-1 text-sm text-cream-100/80">
              Crea tu cuenta gratis y explora el inventario verificado
              que encaja con tu presupuesto. Sin compromiso.
            </p>
          </div>
          <Link
            href="/registro?returnTo=/cuenta"
            className={buttonClasses({
              variant: "primary",
              size: "lg",
              className: "whitespace-nowrap",
            })}
          >
            Crear cuenta
            <ArrowRight className="h-5 w-5" aria-hidden />
          </Link>
        </div>
      </div>

      <p className="text-xs leading-relaxed text-ink-500">
        <strong>Nota:</strong> los números son estimaciones conservadoras
        basadas en condiciones típicas de pre-construcción CONFOTUR en RD
        (20% inicial, 60% durante 24 meses, 20% saldo refinanciable). Cap
        rate {Math.round(0.075 * 100)}% anual es promedio público (TheLatinvestor 2025); los proyectos
        específicos pueden variar. Esto no es asesoría legal ni financiera.
      </p>
    </div>
  );
}

interface SliderFieldProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  formatValue: (n: number) => string;
  hint?: string;
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
  hint,
}: SliderFieldProps) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <label htmlFor={id} className="text-base font-medium text-ink">
          {label}
        </label>
        <span className="text-2xl font-bold tabular-nums text-coral sm:text-3xl">
          {formatValue(value)}
        </span>
      </div>
      <Slider
        id={id}
        className="mt-4"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="mt-2 flex items-center justify-between text-xs text-ink-500">
        <span>{formatValue(min)}</span>
        {hint && <span className="hidden sm:inline">{hint}</span>}
        <span>{formatValue(max)}+</span>
      </div>
    </div>
  );
}

interface BreakdownStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}

function BreakdownStat({ icon, label, value, sub }: BreakdownStatProps) {
  return (
    <div className="rounded-2xl bg-ink-700/50 p-5 ring-1 ring-cream-50/10">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-coral/15 text-coral">
        {icon}
      </div>
      <p className="mt-3 text-xs font-medium uppercase tracking-wider text-cream-100/65">
        {label}
      </p>
      <p className="mt-1 text-xl font-bold tabular-nums text-cream-50">
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-cream-100/60">{sub}</p>}
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
  note?: string;
}

function Stat({ label, value, note }: StatProps) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-cream-100/65">
        {label}
      </p>
      <p className="mt-1 text-3xl font-extrabold tabular-nums text-cream-50">
        {value}
      </p>
      {note && <p className="mt-1 text-sm text-cream-100/70">{note}</p>}
    </div>
  );
}
